//
//  HelpTourTarget.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module renders the Help Tour Target component and locates tour targets.
//

import { cloneElement, useEffect, type FC, type ReactElement, type RefObject } from "react";

/** Attribute the help provider queries to find tour targets. */
export const helpTourTargetAttribute = "data-help-tour-target";

/**
 * Attribute marking a collapsible container, such as an accordion item, whose hidden content may
 * hold tour targets. Its value is the key the owning component expands when asked to reveal it.
 */
export const helpTourRevealAttribute = "data-help-tour-reveal";

/** Event dispatched, bubbling, from a reveal container when the tour needs its content shown. */
export const helpTourRevealEvent = "help-tour-reveal";

/** Supplies the tour target identifier and the single element it marks. */
export type HelpTourTargetProps = {
    id: string;
    /** Renders the child unmarked; use when only one of several repeated elements is the stop. */
    disabled?: boolean;
    children: ReactElement<Record<string, unknown>>;
};

/**
 * Marks an element as a tour stop.
 *
 * Preconditions: The child is a single element that passes data attributes to a DOM node, as
 * Mantine components do.
 * Postconditions: The child renders with the target attribute and is otherwise unchanged. The tour
 * highlights it through a proxy positioned over it, so marking an element never remounts it and
 * any element (a field, a grid, a section) can be a stop.
 */
const HelpTourTarget: FC<HelpTourTargetProps> = ({ id, disabled, children }) =>
    disabled ? children : cloneElement(children, { [helpTourTargetAttribute]: id });

const isDisplayed = (element: Element) => {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < window.innerWidth;
};

/**
 * Returns the first displayed element marked with the tour target id.
 *
 * Postconditions: Elements that are unmounted, have no size (`display: none`, including the
 * responsive `visibleFrom`/`hiddenFrom` props) or sit wholly beside the viewport, as a collapsed
 * AppShell navbar does, are skipped. Elements above or below the fold count, because the tour
 * scrolls them into view.
 */
export const findDisplayedTourTarget = (id: string) =>
    Array.from(document.querySelectorAll(`[${helpTourTargetAttribute}="${CSS.escape(id)}"]`)).find(
        isDisplayed
    );

/**
 * Returns the element a tour step would use: the first displayed one, or else the first hidden one
 * inside a reveal container, which the tour can ask its owner to expand.
 */
export const findTourTarget = (id: string) => {
    const elements = Array.from(
        document.querySelectorAll(`[${helpTourTargetAttribute}="${CSS.escape(id)}"]`)
    );
    return (
        elements.find(isDisplayed) ??
        elements.find(element => element.closest(`[${helpTourRevealAttribute}]`))
    );
};

/**
 * Asks the owner of the reveal container around a hidden target to show it.
 *
 * Postconditions: Dispatches the reveal event, carrying the container's key, from the nearest
 * reveal container; does nothing for a target outside one. Showing it is left to the owner.
 */
export const revealTourTarget = (element: Element) => {
    const container = element.closest(`[${helpTourRevealAttribute}]`);
    container?.dispatchEvent(
        new CustomEvent(helpTourRevealEvent, {
            bubbles: true,
            detail: container.getAttribute(helpTourRevealAttribute)
        })
    );
};

/**
 * Calls `onReveal` with the key of each reveal container inside `ref` that the tour asks to show.
 *
 * Preconditions: `onReveal` should be stable, or the listener is replaced on every change.
 */
export const useHelpTourReveal = (
    ref: RefObject<HTMLElement | null>,
    onReveal: (key: string) => void
) => {
    useEffect(() => {
        const element = ref.current;
        if (!element) {
            return undefined;
        }

        const listener = (event: Event) => {
            const key = (event as CustomEvent<string | null>).detail;
            if (key) {
                onReveal(key);
            }
        };
        element.addEventListener(helpTourRevealEvent, listener);
        return () => element.removeEventListener(helpTourRevealEvent, listener);
    }, [ref, onReveal]);
};

export default HelpTourTarget;
